



import React, { useEffect, useState } from "react";
import {
  getAdminStatistics,
  getDeliveryPartnerStats,
  getOpenTicketCount    // <--- Import the new function
} from "../Component/Servicefile/dashboardService.jsx";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Store,
  Restaurant,
  People,
  ShoppingCart,
  CurrencyRupee,
  SupportAgent,
  Campaign,
  LocalShipping,
  EmojiEvents,
  WorkspacePremium,
  MilitaryTech,
  Star,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  Line,
  LineChart,
  YAxis,
  CartesianGrid,
} from "recharts";

// ---------- THEME (orange) ----------
const PRIMARY = "#ff6b00";
const PRIMARY_DARK = "#c43e00";

const PIE_COLORS = ["#ff6b00", "#ff9800", "#ffb74d", "#ffcc80", "#ffe0b2"];

// Rank badge
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <Avatar sx={{ bgcolor: "#FFD700", width: 34, height: 34 }}>
        <EmojiEvents sx={{ fontSize: 20, color: "#7a5b00" }} />
      </Avatar>
    );
  }
  if (rank === 2) {
    return (
      <Avatar sx={{ bgcolor: "#C0C0C0", width: 34, height: 34 }}>
        <WorkspacePremium sx={{ fontSize: 20, color: "#4a4a4a" }} />
      </Avatar>
    );
  }
  if (rank === 3) {
    return (
      <Avatar sx={{ bgcolor: "#CD7F32", width: 34, height: 34 }}>
        <MilitaryTech sx={{ fontSize: 20, color: "#fff" }} />
      </Avatar>
    );
  }
  return (
    <Avatar sx={{ bgcolor: "#fdebd9", width: 34, height: 34 }}>
      <Star sx={{ fontSize: 18, color: PRIMARY }} />
    </Avatar>
  );
};

// Performance Table Component
const PerformanceTable = ({ title, badge, data, nameLabel, salesLabel, showRating = false }) => (
  <Paper sx={{ p: 3, borderRadius: 4, height: "auto", mt: 3 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Chip
        label={badge}
        size="small"
        sx={{
          bgcolor: "#fff1e0",
          color: PRIMARY_DARK,
          fontWeight: 600,
        }}
      />
    </Stack>

    <Box sx={{ overflowX: "auto" }}>
      <Table sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>Rank</TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>{nameLabel}</TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>{salesLabel}</TableCell>
            {showRating && (
              <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>Rating</TableCell>
            )}
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }} width={180}>
              Performance
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showRating ? 5 : 4} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No data available</Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => {
              const rank = index + 1;
              let progressValue = item.progress || 0;
              if (typeof progressValue === 'string') {
                progressValue = parseFloat(progressValue.replace('%', '')) || 0;
              }
              progressValue = Number(progressValue);

              return (
                <TableRow
                  key={item.name || item.id || index}
                  sx={{
                    "&:last-child td": { border: 0 },
                    ...(rank === 1 && { bgcolor: "#fff8ee" }),
                  }}
                >
                  <TableCell>
                    <RankBadge rank={rank} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{item.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: PRIMARY, fontWeight: 700 }}>
                      {item.sales}
                    </Typography>
                  </TableCell>
                  {showRating && (
                    <TableCell>
                      <Typography>
                        {item.averageRating ? `${Number(item.averageRating).toFixed(1)} ⭐` : "N/A"}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progressValue, 100)}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 5,
                          bgcolor: "#fde6cf",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: PRIMARY,
                            borderRadius: 5,
                          },
                        }}
                      />
                      <Typography variant="body2" fontWeight={600} sx={{ width: 50 }}>
                        {progressValue.toFixed(1)}%
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  </Paper>
);

// KPI Card Component
const KPI = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      height: "100%",
      border: "1px solid #f0e0d0",
      transition: ".3s",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 15px 30px rgba(255,107,0,.18)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="body2" color="text.secondary">
          {item.title}
        </Typography>
        <Typography variant="h5" fontWeight={700} mt={1}>
          {item.value}
        </Typography>
      </Box>
      <Avatar sx={{ bgcolor: PRIMARY, width: 56, height: 56 }}>
        {item.icon}
      </Avatar>
    </Stack>
  </Paper>
);

export default function SuperAdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [riders, setRiders] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [vendorSalesData, setVendorSalesData] = useState([]);
  const [openTickets, setOpenTickets] = useState(0); // <--- State for tickets

  // Dynamic KPIs
  const [kpis, setKpis] = useState([
    { title: "Total Restaurants", value: "0", icon: <Restaurant /> },
    { title: "Total Users", value: "0", icon: <People /> },
    { title: "Total Riders", value: "0", icon: <LocalShipping /> },
    { title: "Total Deliveries", value: "0", icon: <ShoppingCart /> },
    { title: "Total Revenue", value: "₹0", icon: <CurrencyRupee /> },
    { title: "Open Tickets", value: "0", icon: <SupportAgent /> }, // <--- Default 0
  ]);

  useEffect(() => {
    fetchAllStatistics();
  }, []);

  const fetchAllStatistics = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);

      const fromDate = sevenDaysAgo.toISOString().split('T')[0];
      const toDate = today.toISOString().split('T')[0];

      // ✅ Use Promise.allSettled - continues even if one fails
      const results = await Promise.allSettled([
        getAdminStatistics(fromDate, toDate),
        getDeliveryPartnerStats(),
        getOpenTicketCount()
      ]);

      // Extract results with fallbacks
      const foodData = results[0].status === 'fulfilled' ? results[0].value : null;
      const deliveryData = results[1].status === 'fulfilled' ? results[1].value : null;
      const ticketCount = results[2].status === 'fulfilled' ? results[2].value : 0;

      // Log failures
      if (results[0].status === 'rejected') {
        console.error('❌ Failed to fetch food statistics:', results[0].reason);
      }
      if (results[1].status === 'rejected') {
        console.error('❌ Failed to fetch delivery statistics:', results[1].reason);
      }
      if (results[2].status === 'rejected') {
        console.error('❌ Failed to fetch ticket count:', results[2].reason);
      }

      setOpenTickets(ticketCount);
      setDashboardData(foodData);

      // Update KPIs with available data
      setKpis([
        { title: "Total Restaurants", value: foodData?.totalRestaurants || 0, icon: <Restaurant /> },
        { title: "Total Users", value: foodData?.totalUsers || foodData?.totalCustomers || 0, icon: <People /> },
        { title: "Total Riders", value: deliveryData?.totalPartners || 0, icon: <LocalShipping /> },
        { title: "Total Deliveries", value: deliveryData?.totalDeliveries || 0, icon: <ShoppingCart /> },
        { title: "Total Revenue", value: `₹${(foodData?.totalRevenue || 0).toFixed(2)}`, icon: <CurrencyRupee /> },
        { title: "Open Tickets", value: ticketCount, icon: <SupportAgent /> },
      ]);

      // Restaurants (only if foodData exists)
      if (foodData?.topPerformingVendors?.length > 0) {
        const restaurantData = foodData.topPerformingVendors.map((vendor) => ({
          id: vendor.vendorId,
          name: `${vendor.restaurantName}`,
          sales: `₹${Number(vendor.revenue).toFixed(2)}`,
          salesGrowth: vendor.salesGrowth || "0%",
          progress: parseFloat(vendor.percentage) || 0,
        }));
        setRestaurants(restaurantData);
      }

      // Payment Methods
      if (foodData?.paymentMethods?.length > 0) {
        setPaymentMethods(foodData.paymentMethods);
      }

      // Vendor Sales Data
      if (foodData?.vendorSalesGrowth) {
        const vendorIds = Object.keys(foodData.vendorSalesGrowth)
          .map(Number)
          .sort((a, b) => a - b);

        const salesData = vendorIds.map(vendorId => ({
          name: `V${vendorId}`,
          revenue: foodData.vendorSalesGrowth[vendorId],
          vendorId: vendorId,
        }));
        setVendorSalesData(salesData);
      }

      // Riders (only if deliveryData exists)
      if (deliveryData?.topPartners?.length > 0) {
        const riderData = deliveryData.topPartners.map((partner) => ({
          name: partner.partnerName || `Partner ${partner.partnerId}`,
          sales: `₹${Number(partner.totalEarnings || 0).toFixed(2)}`,
          averageRating: partner.averageRating || 0,
          progress: Math.min(Math.max(Number(partner.performanceScore || 0), 0), 100),
        }));
        setRiders(riderData);
      }

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography variant="h6">Loading Dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflow: "hidden",
        p: 3,
        pt: 5,
        boxSizing: "border-box",
        marginTop: '37px',
        background: "#faf8f7",
        minHeight: "100vh",
      }}
    >
      <Grid container spacing={3} sx={{ height: "calc(100vh - 70px)" }}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} lg={9} sx={{ height: "100%" }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              pr: 1,
              scrollbarWidth:"none",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: PRIMARY,
                borderRadius: "10px",
              },
            }}
          >
            {/* KPI CARDS */}
            <Grid container spacing={3}>
              {kpis.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.title}>
                  <KPI item={item} />
                </Grid>
              ))}
            </Grid>

            {/* TOP PERFORMING RESTAURANTS */}
            <PerformanceTable
              title="Top Performing Restaurants"
              badge="Ranked by revenue"
              nameLabel="Restaurant"
              salesLabel="Revenue"
              data={restaurants}
              showRating={false}
            />

            {/* TOP PERFORMING RIDERS */}
            <PerformanceTable
              title="Top Performing Riders"
              badge="Ranked by performance score"
              nameLabel="Rider"
              salesLabel="Earnings"
              data={riders}
              showRating={true}
            />

            <Box sx={{ height: 2 }} />
          </Box>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} lg={3} sx={{ height: "100%" }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              pr: 0.5,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: PRIMARY,
                borderRadius: "10px",
              },
            }}
          >
            <Stack spacing={3}>
              {/* VENDOR SALES LINE CHART */}
              {vendorSalesData.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: 4, backgroundColor: "#e66d3321" }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Vendor Sales
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={vendorSalesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={(value) => `₹${(value / 100000).toFixed(1)}L`}
                      />
                      <Tooltip
                        formatter={(value) => [`₹${Number(value).toFixed(2)}`, "Revenue"]}
                        labelFormatter={(label) => `Vendor ${label.replace('V', '')}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={PRIMARY}
                        strokeWidth={3}
                        dot={{ r: 6, fill: PRIMARY }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {/* PAYMENT METHODS PIE CHART */}
              {paymentMethods.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: 4, backgroundColor: "#e66d3321" }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Payment Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={paymentMethods}
                        dataKey="count"
                        nameKey="method"
                        outerRadius={90}
                        label={({ method, percentage }) => `${method.replace(/_/g, ' ')}: ${percentage}`}
                      >
                        {paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${props.payload.percentage} (${value} orders)`,
                          props.payload.method.replace(/_/g, ' ')
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}